import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import AuthService from "../../api/services/AuthService";
import { ProfileContext } from "../../context/ProfileProvider";
import moment from "moment";
import "../../ui/Pages/ReferalPage/referralHistoryTree.css";
import "../../ui/Pages/ReferalPage/referralHistoryV2.css";

const referralRowKey = (item) => item?.user?._id ?? item?._id ?? item?.masked_id;

/** GET referral-children: body may be `data: [...]`, `data: { items: [] }`, or `data: { data: [] }`. */
const normalizeReferralChildrenList = (d) => {
  if (d == null) return [];
  if (Array.isArray(d)) return d;
  if (Array.isArray(d.items)) return d.items;
  if (Array.isArray(d.data)) return d.data;
  return [];
};

const parentIdForDownline = (item) => {
  const id = item?.user?._id ?? item?._id;
  return id == null || id === "" ? null : String(id);
};

/** Show Load downline while backend says more may exist; hide only when hasMore is explicitly false. */
const canShowLoadDownline = (item) => {
  const id = parentIdForDownline(item);
  if (id == null) return false;
  return item?.hasMore !== false;
};

/**
 * Reusable referral history section: summary boxes (incl. Total earnings) + referral tree list
 * (desktop + mobile). Self-fetches all data. Used on /refer_earn and the Earning page tab.
 */
const ReferralHistorySection = ({ showHowItWorks = true }) => {
  const { userDetails } = useContext(ProfileContext) || {};
  const token = localStorage.getItem("token");

  const [refferalList, setRefferalList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [signupRewardData, setSignupRewardData] = useState(null);

  const [downlineByParent, setDownlineByParent] = useState({});
  const [downlineLoading, setDownlineLoading] = useState({});
  const [downlineError, setDownlineError] = useState({});

  const [referralEarningsState, setReferralEarningsState] = useState({
    items: [],
    totalEarned: null,
    earningsShortName: "USDT",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [expandedNodeIds, setExpandedNodeIds] = useState(() => new Set());

  const loadReferralEarnings = useCallback(async () => {
    if (!token) return;
    try {
      const res = await AuthService.getMyReferralEarnings();
      if (res?.success && res?.data != null) {
        const d = res.data;
        const items = Array.isArray(d.items) ? d.items : [];
        const first = items[0];
        setReferralEarningsState({
          items,
          totalEarned: d.totalEarned != null ? Number(d.totalEarned) : null,
          earningsShortName: (first?.shortName && String(first.shortName).trim()) || "USDT",
        });
      } else {
        setReferralEarningsState({ items: [], totalEarned: null, earningsShortName: "USDT" });
      }
    } catch {
      setReferralEarningsState({ items: [], totalEarned: null, earningsShortName: "USDT" });
    }
  }, [token]);

  const handleReferalList = useCallback(async () => {
    if (!token) return;
    setListLoading(true);
    setListError(null);
    setDownlineByParent({});
    setDownlineError({});
    setExpandedNodeIds(new Set());
    try {
      const result = await AuthService.getMyReferralTree();
      if (result?.success && result?.data != null) {
        const d = result.data;
        const items = Array.isArray(d.items) ? d.items : Array.isArray(d) ? d : [];
        setRefferalList(items);
      } else {
        setRefferalList([]);
        setListError(result?.message || "Failed to load referral list");
      }
    } catch (err) {
      setRefferalList([]);
      setListError(err?.message || "Something went wrong");
    } finally {
      setListLoading(false);
    }
  }, [token]);

  const loadReferralDownline = useCallback(async (parentId) => {
    if (parentId == null || parentId === "") return;
    const pid = String(parentId);
    setDownlineLoading((p) => ({ ...p, [pid]: true }));
    setDownlineError((p) => ({ ...p, [pid]: null }));
    try {
      const res = await AuthService.getReferralChildren(pid);
      if (res?.success) {
        const list = normalizeReferralChildrenList(res.data);
        setDownlineByParent((p) => ({ ...p, [pid]: list }));
      } else {
        setDownlineError((p) => ({ ...p, [pid]: res?.message || "Failed to load downline" }));
      }
    } catch (e) {
      setDownlineError((p) => ({ ...p, [pid]: e?.message || "Something went wrong" }));
    } finally {
      setDownlineLoading((p) => ({ ...p, [pid]: false }));
    }
  }, []);

  const loadSignupRewardHistory = useCallback(async () => {
    if (!token) return;
    const userId = userDetails?.userId || localStorage.getItem("userId");
    if (!userId) return;
    try {
      const res = await AuthService.getSignupRewardHistory(userId);
      if (res?.success && res?.data != null) {
        setSignupRewardData(res.data);
      } else {
        setSignupRewardData(null);
      }
    } catch {
      setSignupRewardData(null);
    }
  }, [token, userDetails?.userId]);

  useEffect(() => {
    handleReferalList();
  }, [handleReferalList]);

  useEffect(() => {
    loadReferralEarnings();
  }, [loadReferralEarnings]);

  useEffect(() => {
    loadSignupRewardHistory();
  }, [loadSignupRewardHistory]);

  const getDisplayName = (item) => {
    if (item?.user) {
      const u = item.user;
      const n = `${(u.firstName || "").trim()} ${(u.lastName || "").trim()}`.trim();
      if (n) return n;
      const e = (u.emailId || u.email || "").trim();
      if (e) {
        const at = e.indexOf("@");
        return at > 0 ? e.slice(0, at) : e;
      }
      if (u.referralCode) return u.referralCode;
      return u.uuid || "—";
    }
    const name = item?.full_name_masked ?? `${item?.firstName ?? ""} ${item?.lastName ?? ""}`.trim();
    return name || "—";
  };
  const getDisplayId = (item) => {
    if (item?.user) {
      const u = item.user;
      return u.uuid || u.referralCode || u._id || "—";
    }
    return item?.masked_id ?? item?.uuid ?? "—";
  };
  const getJoinDate = (item) => {
    const d = item?.signup_date ?? item?.user?.createdAt ?? item?.createdAt;
    return d ? moment(d).format("DD/MM/YYYY") : "—";
  };
  const getJoinDateTime = (item) => {
    const d = item?.signup_date ?? item?.user?.createdAt ?? item?.createdAt;
    return d ? moment(d).format("DD/MM/YYYY hh:mm A") : "—";
  };
  const getKycStatus = (item) => {
    if (item?.kycStatus) return item.kycStatus;
    if (item?.kyc_status) return item.kyc_status;
    const kv = item?.kycVerified ?? item?.user?.kycVerified;
    if (kv === 2) return "Verified";
    if (kv === 1) return "Pending";
    if (kv === 0) return "Not Verified";
    return "Not Verified";
  };

  const filteredList = (refferalList ?? []).filter((item) => {
    if (!searchTerm?.trim()) return true;
    const q = searchTerm.trim().toLowerCase();
    if (item?.user) {
      const u = item.user;
      const blob = [u.referralCode, u.uuid, u.emailId, u.firstName, u.lastName, u._id].filter(Boolean).join(" ").toLowerCase();
      return blob.includes(q);
    }
    const maskedId = item?.masked_id ?? item?.uuid ?? "";
    const fullName = item?.full_name_masked ?? `${item?.firstName ?? ""} ${item?.lastName ?? ""}`.trim();
    return maskedId?.toLowerCase().includes(q) || fullName?.toLowerCase().includes(q);
  });

  const earningsByFromUserId = useMemo(() => {
    const m = {};
    for (const e of referralEarningsState.items) {
      const id = e?.fromUserId != null ? String(e.fromUserId) : "";
      if (!id) continue;
      const amt = Number(e.amount) || 0;
      const sn = (e.shortName || "").trim();
      const lv = Number(e.level) || 0;
      if (!m[id]) m[id] = { amount: 0, shortName: sn, maxLevel: 0 };
      m[id].amount += amt;
      if (sn) m[id].shortName = sn;
      m[id].maxLevel = Math.max(m[id].maxLevel, lv);
    }
    return m;
  }, [referralEarningsState.items]);

  const formatEarningForRow = (rowKey) => {
    if (rowKey == null || rowKey === "") return "—";
    const e = earningsByFromUserId[String(rowKey)];
    if (!e) return "—";
    const sn = e.shortName || referralEarningsState.earningsShortName || "USDT";
    return `${e.amount} ${sn}`.trim();
  };

  const isKycVerifiedUser = (item) => {
    const s = getKycStatus(item)?.toLowerCase() || "";
    if (s.includes("not") && s.includes("verif")) return false;
    if (s.includes("approved") || s.includes("verified")) return true;
    const kv = item?.kycVerified ?? item?.user?.kycVerified;
    return kv === 2;
  };

  const summaryStats = useMemo(() => {
    const list = refferalList || [];
    const totalReferrals = list.length;
    const verifiedUsers = list.filter((it) => isKycVerifiedUser(it)).length;
    const pendingKyc = list.filter((it) => !isKycVerifiedUser(it)).length;
    const te = referralEarningsState.totalEarned;
    const sym = referralEarningsState.earningsShortName || "USDT";
    const signupBonus = signupRewardData?.signupBonus;
    const signupCurrency =
      (signupRewardData?.currencyShortName && String(signupRewardData.currencyShortName).trim()) ||
      signupRewardData?.history?.[0]?.shortName ||
      "USDT";
    const signupClaimed = Boolean(signupRewardData?.status?.rewardClaimed);
    return {
      totalReferrals,
      verifiedUsers,
      totalEarned: te != null ? te : 0,
      earnSymbol: sym,
      pendingKyc,
      signupBonus: signupBonus != null ? Number(signupBonus) : null,
      signupCurrency,
      signupClaimed,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    refferalList,
    referralEarningsState.totalEarned,
    referralEarningsState.earningsShortName,
    signupRewardData,
  ]);

  const getInitials = (item) => {
    const name = getDisplayName(item);
    if (!name || name === "—") return "?";
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
    return name.replace(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase() || "?";
  };

  const earningsMetaForKey = (rowKey) => {
    if (rowKey == null) return { amount: 0, shortName: "", maxLevel: 0 };
    return (
      earningsByFromUserId[String(rowKey)] || {
        amount: 0,
        shortName: referralEarningsState.earningsShortName || "USDT",
        maxLevel: 0,
      }
    );
  };

  const canExpandNode = (item, rowKey) => {
    const id = String(rowKey);
    const ch = downlineByParent[id];
    if (Array.isArray(ch) && ch.length > 0) return true;
    return canShowLoadDownline(item);
  };

  const toggleNodeExpanded = useCallback(
    (item) => {
      const id = parentIdForDownline(item);
      if (!id) return;
      setExpandedNodeIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
          const ch = downlineByParent[id];
          const loading = downlineLoading[id];
          if (ch === undefined && !loading && canShowLoadDownline(item)) {
            loadReferralDownline(id);
          }
        }
        return next;
      });
    },
    [downlineByParent, downlineLoading, loadReferralDownline]
  );

  const referralTableRows = useMemo(() => {
    const rows = [];
    const walk = (item, depth) => {
      const k = referralRowKey(item) ?? `anon-${rows.length}`;
      const keyStr = k != null ? String(k) : `anon-${rows.length}`;
      rows.push({ item, depth, rowKey: keyStr });
      if (!expandedNodeIds.has(keyStr)) return;
      const children = downlineByParent[keyStr] ?? downlineByParent[k];
      if (Array.isArray(children) && children.length > 0) {
        for (const c of children) {
          walk(c, depth + 1);
        }
      }
    };
    (filteredList || []).forEach((it) => walk(it, 0));
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredList, downlineByParent, expandedNodeIds]);

  const displayedReferralRows = useMemo(() => {
    if (levelFilter === "all") return referralTableRows;
    const want = Number(levelFilter);
    if (Number.isNaN(want)) return referralTableRows;
    return referralTableRows.filter((row) => {
      const em = earningsByFromUserId[String(row.rowKey)] || { maxLevel: 0 };
      const levelNum = em.maxLevel > 0 ? em.maxLevel : Math.min(12, row.depth + 1);
      return levelNum === want;
    });
  }, [referralTableRows, levelFilter, earningsByFromUserId]);

  /** S. No. only for direct referrals (depth 0); downline children get no number. */
  const directReferralSnoByIndex = useMemo(() => {
    const out = [];
    let c = 0;
    for (let i = 0; i < displayedReferralRows.length; i++) {
      if (displayedReferralRows[i].depth === 0) {
        c += 1;
        out[i] = c;
      } else {
        out[i] = null;
      }
    }
    return out;
  }, [displayedReferralRows]);

  return (
    <div className="referral_recent_s rh-referral-premium">
      <div className="container">
        <div className="dashboard_recent_s">
          <div className="rh-page-header">
            <div className="rh-page-header__intro">
              <h4 className="rh-page-header__title">Referral History</h4>
              {token && (
                <p className="rh-page-header__subtitle text-muted small mb-0">
                  Explore your referral network, track earnings and view levels.
                </p>
              )}
            </div>
            <div className="rh-page-header__controls">
              <div className="searchBar custom-tabs">
                <i className="ri-search-2-line" aria-hidden />
                <input
                  type="search"
                  className="custom_search"
                  placeholder="Search name or ID"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Search referrals"
                />
              </div>
            </div>
          </div>

          {token && !listLoading && (
            <div className="rh-summary-grid">
              <div className="rh-stat-card rh-stat-card--signup">
                <div className="rh-stat-card__label">Signup bonus</div>
                <div className="rh-stat-card__value">
                  {summaryStats.signupBonus != null
                    ? `${summaryStats.signupBonus} ${summaryStats.signupCurrency}`
                    : "—"}
                </div>
                <div className="rh-stat-card__hint">
                  {summaryStats.signupClaimed ? "Reward claimed" : "Complete tasks to claim"}
                </div>
              </div>
              <div className="rh-stat-card">
                <div className="rh-stat-card__label">Total referrals</div>
                <div className="rh-stat-card__value">{summaryStats.totalReferrals}</div>
                <div className="rh-stat-card__hint">Direct from your code</div>
              </div>
              <div className="rh-stat-card">
                <div className="rh-stat-card__label">Verified users</div>
                <div className="rh-stat-card__value text-success">
                  {summaryStats.verifiedUsers}
                </div>
                <div className="rh-stat-card__hint">KYC completed</div>
              </div>
              <div className="rh-stat-card rh-stat-card--earn">
                <div className="rh-stat-card__label">Total earnings</div>
                <div className="rh-stat-card__value">
                  {summaryStats.totalEarned} {summaryStats.earnSymbol}
                </div>
                <div className="rh-stat-card__hint">From all levels</div>
              </div>
              <div className="rh-stat-card rh-stat-card--pending">
                <div className="rh-stat-card__label">Pending rewards</div>
                <div className="rh-stat-card__value">{summaryStats.pendingKyc}</div>
                <div className="rh-stat-card__hint">Direct refs awaiting KYC</div>
              </div>
            </div>
          )}

          {listError && (
            <div className="alert alert-warning mb-3" role="alert">
              {listError}
            </div>
          )}

          {/* Desktop tree */}
          <div className="table-responsive desktop_view2 d-none d-md-block">
            {listLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-2 mb-0">Loading referral history...</p>
              </div>
            ) : filteredList?.length > 0 ? (
              <div className="rh-list-shell">
                {levelFilter !== "all" && displayedReferralRows.length === 0 ? (
                  <div className="rh-empty-filter text-center py-5 text-muted small">
                    No referrals at this level. Try <strong>All Levels</strong> or another filter.
                  </div>
                ) : (
                  <div className="rh-list-scroll">
                  <div className="rh-list" role="tree" aria-label="Referral network">
                    {displayedReferralRows.map((row, index) => {
                      const sno = directReferralSnoByIndex[index];
                      const { item, depth, rowKey: rk } = row;
                      const dErr = downlineError[rk] ?? downlineError[String(rk)];
                      const dLoad = !!(downlineLoading[rk] ?? downlineLoading[String(rk)]);
                      const showCh = canExpandNode(item, rk);
                      const isEx = expandedNodeIds.has(String(rk));
                      const em = earningsMetaForKey(rk);
                      const levelNum = em.maxLevel > 0 ? em.maxLevel : Math.min(12, depth + 1);
                      const earnedStr = formatEarningForRow(rk);
                      const hasEarned = earnedStr !== "—";
                      return (
                        <div
                          key={`d-${rk}-${depth}-${index}`}
                          className="rh-row"
                          data-depth={depth}
                          style={{ animationDelay: `${Math.min(index * 45, 400)}ms` }}
                        >
                          {sno != null ? (
                            <div
                              className="rh-row__sno"
                              title={`S. No. ${sno}`}
                              aria-label={`Serial number ${sno}`}
                            >
                              {sno}
                            </div>
                          ) : (
                            <div className="rh-row__sno rh-row__sno--empty" aria-hidden />
                          )}
                          <div className="rh-row__gutter" style={{ minWidth: 12 + depth * 20 }}>
                            <div className="rh-row__guides" aria-hidden>
                              {Array.from({ length: depth }).map((_, di) => (
                                <span key={String(di)} className="rh-tree-vline" />
                              ))}
                            </div>
                            {showCh ? (
                              <button
                                type="button"
                                className="rh-expand"
                                onClick={() => toggleNodeExpanded(item)}
                                disabled={dLoad}
                                aria-expanded={isEx}
                                title={isEx ? "Collapse" : "Expand downline"}
                              >
                                {dLoad ? (
                                  <span className="spinner-border spinner-border-sm" role="status" />
                                ) : (
                                  <i className={`ri-arrow-right-s-line ${isEx ? "rh-expand--open" : ""}`} aria-hidden />
                                )}
                              </button>
                            ) : (
                              <span className="rh-expand rh-expand--spacer" aria-hidden />
                            )}
                          </div>
                          <div className="rh-avatar rh-avatar--brand">{getInitials(item)}</div>
                          <div className="rh-row__main">
                            <div className="rh-name">{getDisplayName(item)}</div>
                            <div className="rh-meta">
                              <span className="rh-meta__id">ID {getDisplayId(item)}</span>
                              <span className="rh-meta__dot" aria-hidden>
                                ·
                              </span>
                              <span className="rh-meta__date">Joined {getJoinDate(item)}</span>
                            </div>
                            {dErr ? <div className="text-danger small mt-1">{dErr}</div> : null}
                          </div>
                          <div className="rh-row__badges">
                            {isKycVerifiedUser(item) ? (
                              <span className="rh-badge rh-badge--kyc">
                                <i className="ri-shield-check-line" aria-hidden />
                                Verified
                              </span>
                            ) : (
                              <span className="rh-badge rh-badge--kyc-p">KYC {getKycStatus(item)}</span>
                            )}
                            <span className="rh-badge rh-badge--lvl">L{levelNum}</span>
                          </div>
                          <div className="rh-row__earn">
                            {hasEarned ? (
                              <span className="rh-earn rh-earn--yes">+{earnedStr}</span>
                            ) : (
                              <span className="rh-earn rh-earn--no">—</span>
                            )}
                          </div>
                          <div className="rh-row__chev" aria-hidden>
                            <i className="ri-arrow-right-s-line" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="no-data-wrapper">
                <div className="no_data_vector">
                  <img src="/images/no_data_vector.svg" className="img-fluid dark_img" width="96" height="96" alt="No data" /><img src="/images/no_data_vector_light.png" className="img-fluid light_img" alt="No data" />
                </div>
                <p className="mt-2 mb-0">No referrals yet. Share your link to get started!</p>
              </div>
            )}
          </div>

          {/* Mobile */}
          <div className="order_history_mobile_view d-md-none">
            <div className="rh-m-toolbar d-flex flex-column gap-2 mb-2">
              <div className="d-flex flex-wrap align-items-stretch gap-2">
                <div className="searchBar custom-tabs flex-grow-1" style={{ minWidth: 0 }}>
                  <i className="ri-search-2-line" aria-hidden />
                  <input
                    type="search"
                    className="custom_search"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-label="Search referrals"
                  />
                </div>
                <select
                  className="form-select form-select-sm rh-level-filter w-auto flex-shrink-0"
                  value={levelFilter}
                  onChange={(e) => setLevelFilter(e.target.value)}
                  aria-label="Filter by level"
                >
                  <option value="all">All Levels</option>
                  <option value="1">L1</option>
                  <option value="2">L2</option>
                  <option value="3">L3</option>
                  <option value="4">L4</option>
                </select>
              </div>
            </div>
            <div className="d-flex flex-column gap-2">
              {listLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" style={{ width: "1.5rem", height: "1.5rem" }} />
                  <p className="mt-2 mb-0 small">Loading...</p>
                </div>
              ) : referralTableRows?.length > 0 ? (
                levelFilter !== "all" && displayedReferralRows.length === 0 ? (
                  <div className="rh-empty-filter text-center py-4 text-muted small px-2">No members at this level. Try All Levels.</div>
                ) : (
                  <div className="rh-m-list-scroll">
                  {displayedReferralRows.map((row, index) => {
                    const sno = directReferralSnoByIndex[index];
                    const { item, depth, rowKey: rk } = row;
                    const dErr = downlineError[rk] ?? downlineError[String(rk)];
                    const dLoad = !!(downlineLoading[rk] ?? downlineLoading[String(rk)]);
                    const showCh = canExpandNode(item, rk);
                    const isEx = expandedNodeIds.has(String(rk));
                    const em = earningsMetaForKey(rk);
                    const levelNum = em.maxLevel > 0 ? em.maxLevel : Math.min(12, depth + 1);
                    const earnedStr = formatEarningForRow(rk);
                    const hasEarned = earnedStr !== "—";
                    return (
                      <div
                        key={`m-${rk}-${depth}-${index}`}
                        className="rh-m-card"
                        data-depth={depth}
                        style={{ marginLeft: depth * 10, animationDelay: `${Math.min(index * 40, 350)}ms` }}
                      >
                        <div className="d-flex flex-row align-items-start gap-2 w-100">
                          {sno != null ? (
                            <div className="rh-m-sno text-muted" title={`S. No. ${sno}`} aria-label={`Serial number ${sno}`}>
                              {sno}
                            </div>
                          ) : (
                            <div className="rh-m-sno rh-m-sno--empty" aria-hidden />
                          )}
                          <div className="d-flex flex-column align-items-center flex-shrink-0" style={{ width: 36 }}>
                            {showCh ? (
                              <button
                                type="button"
                                className="rh-expand"
                                onClick={() => toggleNodeExpanded(item)}
                                disabled={dLoad}
                                aria-expanded={isEx}
                              >
                                {dLoad ? (
                                  <span className="spinner-border spinner-border-sm" role="status" />
                                ) : (
                                  <i className={`ri-arrow-right-s-line ${isEx ? "rh-expand--open" : ""}`} aria-hidden />
                                )}
                              </button>
                            ) : (
                              <span className="rh-expand rh-expand--spacer" style={{ width: 36, height: 36 }} />
                            )}
                          </div>
                          <div className="rh-avatar rh-avatar--brand rh-avatar--sm">{getInitials(item)}</div>
                          <div className="flex-grow-1 min-w-0">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-1">
                              <div className="rh-m-title text-break fw-semibold">
                                {depth > 0 && <span className="text-muted me-1">└</span>}
                                {getDisplayName(item)}
                              </div>
                              {hasEarned ? (
                                <span className="rh-earn rh-earn--yes small">+{earnedStr}</span>
                              ) : (
                                <span className="rh-earn rh-earn--no small">—</span>
                              )}
                            </div>
                            <div className="rh-badges mb-2">
                              {isKycVerifiedUser(item) ? (
                                <span className="rh-badge rh-badge--kyc">
                                  <i className="ri-shield-check-line" aria-hidden />
                                  Verified
                                </span>
                              ) : (
                                <span className="rh-badge rh-badge--kyc-p">KYC {getKycStatus(item)}</span>
                              )}
                              <span className="rh-badge rh-badge--lvl">L{levelNum}</span>
                            </div>
                            <div className="rh-m-grid">
                              <span>User ID</span>
                              <span className="text-break">{getDisplayId(item)}</span>
                              <span>Join date</span>
                              <span>{getJoinDateTime(item)}</span>
                            </div>
                            {dErr ? <div className="text-danger small mt-1">{dErr}</div> : null}
                          </div>
                          <div className="text-muted flex-shrink-0 pt-1 d-none d-sm-block" aria-hidden>
                            <i className="ri-arrow-right-s-line" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                )
              ) : (
                <div className="no-data-wrapper w-100">
                  <div className="no_data_vector">
                    <img src="/images/no_data_vector.svg" className="img-fluid dark_img" width="96" height="96" alt="No data" /><img src="/images/no_data_vector_light.png" className="img-fluid light_img" alt="No data" />
                    <p className="mt-2 mb-0 small">No referrals yet</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showHowItWorks && token && !listLoading && (
            <div className="rh-how-it-works">
              <h5 className="rh-how-it-works__title">How it works</h5>
              <ol className="rh-how-it-works__list">
                <li>Refer users with your code</li>
                <li>They complete KYC</li>
                <li>You earn by level</li>
                <li>Rewards release as per vesting</li>
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralHistorySection;
